//imports
import BotonAbrirAula, { tieneAula } from "./abrirAulaBoton";
import BotonPortalClase from "./portalClaseBoton";
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { disableIdleLockSwitch, enableIdleLockSwitch, displayNotice, RootState, AppDispatch } from '../../../../globals';
import { Column } from '../../../../components/commonTable';
import { ScheduleDTO } from '../../../../models/ScheduleDTO';
import { DataInfoModal, ItemCount, Page } from './interfaces';
import { handleMentorsSalaryXlsx } from '../../../../redux/reducers/mentorsReducer';

//services
import HttpManager from '../../../../services/HttpManager';

//view
import AttendancesView from './attendancesView';
import Constants, { FILTER_TYPES, ROLE, scheduleStatus } from '../../../../constants';
import { setPermissions } from '../../../../redux/reducers/sessionReducer';
import { cursoCorto } from '../../../../utils/cursoCorto';
import { DayClassDTO, DaySummaryDTO } from '../../../../models/DaySummaryDTO';
import { horarioBonito } from '../../../../utils/horarioBonito';
import { ahoraCdmx } from "../../../../utils/horaMexico";

/*
	Los tres interruptores con los que abre Asistencias. El tipo vive aqui porque
	es de este modulo: la barra de filtros ya es compartida y no sabe de estos
	nombres. (Esteban, 2026-09-16)
*/
type Rapidos = {hoy : boolean, vigentes : boolean, conAlumnos : boolean};

//messages
const noStudentsForSchedule = "No hay estudiantes registrados para este horario";
const noCampuses = "No hay planteles registrados o asignados al rol en uso por el usuario, por lo que el acceso a este módulo permanecerá deshabilitado.";
const initError = "La búsqueda iniclal de datos no pudo ser completada.";
const unlinkedClassroomsFoundMessage = "Algunos horarios pertenecen a salones sin edificio o periodo asociado, o el periodo asociado no pertenece a ningún plantel, " + "por lo que no se podrá acceder a esta sección.";

//constants
const pagesSize: number[] = [100, 50, 20, 10];   // 100 por página por defecto (Esteban, 2026-09-11)
const mentorItemsSize: number = 100;

const AttendancesController = () => {

    //init dispatch
    const dispatch = useDispatch<AppDispatch>();

    //init http
    const http = HttpManager.getInstance();

    //useSelector
    const sessionToken: string | null = useSelector((state: RootState) => state.sessionToken.value);
    const currentUserCampus: any = useSelector((state: RootState) => state.currentUserCampus.value);
    const userProfile: any = useSelector((state: RootState) => state.userProfile.value);
    const campusRef: any = useSelector((state: RootState) => state.campusRef.value);
    const idleLocked: boolean = useSelector((state: RootState) => state.idleLockSwitch.value);
    const currentRole: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentRole);

    //useState
    const [searchValue, setSearchValue] = useState<string>("");
    const [filters, setFilters] = useState<{ [key: string]: any }>({});
    const [sectionLoaded, setSectionLoaded] = useState<boolean>(false);
    const [schedules, setSchedules] = useState<ScheduleDTO[] | []>([]);
    const [userCampus, setUserCampus] = useState<number>(currentUserCampus);
    const [selectedSchedule, setSelectedSchedule] = useState<ScheduleDTO | null>(null);
    const [noStudentsEnrolledForSchedule, setNoStudentsEnrolledForSchedule] = useState<number>(0);
    const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
    const [dataInfoModal, setDataInfoModal] = useState<DataInfoModal>({});
    const [itemCount, setItemCount] = useState<ItemCount>({
        start: 0,
        end: 0,
        total: 0
    });
    const [pages, setPages] = useState<Page[]>([
        {
            "url": null,
            "label": "&laquo; Previous",
            "active": false
        },
        {
            "url": "",
            "label": "1",
            "active": true
        },
        {
            "url": null,
            "label": "Next &raquo;",
            "active": false
        }
    ]);
    const [itemsPerPage, setItemsPerPage] = useState<number>(pagesSize[0]);
    const [showReportModal, setShowReportModal] = useState<boolean>(false);
    /*
        El tablero "quien ya paso lista hoy". Pide su propia lista del dia entero,
        sin paginar: la cuenta por jornada no sale de una pagina suelta.
        (Esteban, 2026-09-20)
    */
    const [resumenDelDia, setResumenDelDia] = useState<DaySummaryDTO | null>(null);
    const [cargandoDia, setCargandoDia] = useState<boolean>(false);
    /*
        Que dia esta mirando el tablero. Null = hoy, que es como abre.
        Esteban (2026-09-21): "el default asi esta bien pero deberian poderse
        ver dias anteriores".
    */
    const [diaDelTablero, setDiaDelTablero] = useState<string | null>(null);

    /*
        Enlace directo a un horario: /attendances?horario=<id>. Lo usa el calendario
        diario, donde cada clase tiene su boton de "Asistencias". (Esteban, 2026-09-21)
    */
    const [parametrosUrl, setParametrosUrl] = useSearchParams();
    const enlaceAtendido = useRef<string | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(ahoraCdmx());
    const [endDate, setEndDate] = useState<Date | null>(null);

    const fetchRoleData = async () => {
        try {
            const response = await http.roleService.getRoleData(currentRole.id);
            dispatch(setPermissions(response.data.permissions));
            return response.data;
        } catch (err: any) {
            console.error(err);
        }
    }

    const onChangeSearch = (newSearchValue: string) => {
        setSearchValue(newSearchValue);
    }

    const onClickSearch = () => {
        setFilters({ ...filters, search: searchValue });
    }

    const onClearSearch = () => {
        setSearchValue("");
        setFilters({});
    }

    const onClearFilters = () => {
        setSearchValue("");
        setFilters({});
    }

    const updateFilters = (prevFilters: any, columnId: string, value: any) => {
        const updatedFilters = { ...prevFilters };

        if (value === null) {
            if (updatedFilters[columnId]) {
                delete updatedFilters[columnId];
            }
        } else {
            updatedFilters[columnId] = value;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                delete updatedFilters[columnId];
            } else {
                updatedFilters[columnId] = value;
            }
        }

        return JSON.stringify(updatedFilters) === JSON.stringify(prevFilters) ? prevFilters : updatedFilters;
    };

    const onFilterChange = (columnId: string, value: any) => {
        setFilters((prevFilters) => {
            const newFilters = updateFilters(prevFilters, columnId, value);
            return newFilters;
        });
    };

    const updatePaginationInfo = (pagination: any) => {
        const updatedPages = pagination.links.map((page: any) => ({
            ...page,
            onClick: () => {
                if (page.url) {
                    getSchedules(page.url);
                }
            }
        }));
        setItemCount({
            start: pagination.from,
            end: pagination.to,
            total: pagination.total
        });
        setPages(updatedPages);
    }

    //consts
    /*
        Antes eran trece columnas y ni a pantalla completa cabian. Se fueron
        "Meses a facturar" y "Sesiones" (las horas calculadas, que traen errores),
        "Modulo" se lee dentro del horario, y los pares Edificio/Aula e Inicio/Fin
        se juntaron. Las de gestion solo las ve el personal administrativo: al
        profesor no hay que decirle quien es el profesor. (Esteban, 2026-09-16)
    */
    const esMentor: boolean = userProfile.currentRole === ROLE.MENTOR;

    /*
        La lista abria con los 1 135 horarios del plantel, del mas nuevo al mas
        viejo: acabados, sin alumnos y de cualquier dia, todos revueltos. Ahora
        abre con lo que de verdad se consulta. (Esteban, 2026-09-16)
    */
    const [rapidos, setRapidos] = useState<Rapidos>({ hoy: true, vigentes: true, conAlumnos: true });
    const [panelFiltros, setPanelFiltros] = useState<boolean>(false);

    //En la base el dia de la semana empieza en LUNES (0), no en domingo.
    const diaDeHoy: number = (ahoraCdmx().getDay() + 6) % 7;

    const criteriosRapidos = (): any => {
        const criterio: any = {};

        if (rapidos.hoy) {
            criterio.weekDay = String(diaDeHoy);
            criterio.orderBy = "jornada";
        }
        if (rapidos.vigentes)   criterio.inForce = true;
        if (rapidos.conAlumnos) criterio.hasActiveEnrollments = true;

        return criterio;
    };

    //Todas las columnas llevan el contenido a la izquierda, asi que el titulo tambien.
    const tituloALaIzquierda = () => ({ textAlign: "left" as const });

    /*
        Con pocas columnas la tabla las repartia a lo ancho de la pantalla y quedaban
        a un palmo unas de otras: se perdia el renglon al leerlo. Con un ancho por
        columna, el sobrante se lo queda "Horario", que es el texto mas largo.
    */
    const ancho = (medida: string) => () => ({ width: medida, textAlign: "left" as const });

    /* El backend manda "Sá 16:00-19:30, Do 12:00-15:30": dos letras del dia y la hora.
       Las horas traen errores, asi que de aqui solo se usa el DIA. */
    const DIAS_CORTOS = ["Do", "Lu", "Ma", "Mi", "Ju", "Vi", "Sá"];

    const leTocaHoy = (sesiones: string | null | undefined): boolean => {
        if (!sesiones) return false;
        const hoy = DIAS_CORTOS[ahoraCdmx().getDay()];
        return String(sesiones).split(",").some(trozo => trozo.trim().startsWith(hoy));
    };

    const columnasComunes: Column[] = [
        {
            overrideHeaderStyle: ancho("145px"),
            overrideCellStyle: ancho("145px"),
            id: "course",
            header: "Curso",
            accessor: "",
            render: (schedule: ScheduleDTO) => cursoCorto(schedule.course),
            filterType: FILTER_TYPES.INPUT_TEXT
        },
        {
            overrideHeaderStyle: ancho("110px"),
            overrideCellStyle: ancho("110px"),
            id: "period",
            header: "Periodo",
            accessor: "period",
            filterType: FILTER_TYPES.INPUT_TEXT
        },
        {
            overrideHeaderStyle: ancho("205px"),
            overrideCellStyle: ancho("205px"),
            id: "name",
            header: "Horario",
            accessor: "",
            render: (schedule: ScheduleDTO) => horarioBonito(schedule.name),
            filterType: FILTER_TYPES.INPUT_TEXT
        }
    ];

    const columnasDeGestion: Column[] = [
        {
            overrideHeaderStyle: ancho("130px"),
            overrideCellStyle: ancho("130px"),
            id: "mentor",
            header: "Profesor",
            accessor: "",
            render: (schedule: ScheduleDTO) =>
                [schedule.mentorFirstName, schedule.mentorLastName]
                    .map(parte => String(parte || "").trim().split(/\s+/)[0] || "")
                    .filter(Boolean)
                    .join(" ") || schedule.mentor,
            filterType: FILTER_TYPES.INPUT_TEXT
        },
        {
            overrideHeaderStyle: ancho("120px"),
            overrideCellStyle: ancho("120px"),
            id: "code",
            header: "Código",
            accessor: "code",
            filterType: FILTER_TYPES.INPUT_TEXT
        },
        {
            overrideHeaderStyle: ancho("130px"),
            overrideCellStyle: ancho("130px"),
            id: "classroom",
            header: "Aula",
            accessor: "",
            render: (schedule: ScheduleDTO) =>
                [schedule.building, schedule.classroom].filter(Boolean).join(" · "),
            filterType: FILTER_TYPES.INPUT_TEXT
        },
        {
            overrideHeaderStyle: ancho("88px"),
            overrideCellStyle: ancho("88px"),
            id: "status",
            header: "Estatus",
            accessor: "",
            render: (schedule: ScheduleDTO) => {
                return (
                    <span style={{ color: schedule.status === 'Activo' ? "#1E7A4D" : "var(--wa-oro)" }}>
                        {
                            schedule.status
                        }
                    </span>
                );
            }
        },
        {
            overrideHeaderStyle: ancho("150px"),
            overrideCellStyle: ancho("150px"),
            id: "validity",
            header: "Vigencia",
            accessor: "",
            render: (schedule: ScheduleDTO) =>
                [schedule.startDate, schedule.endDate]
                    .filter(Boolean)
                    .map(fecha => String(fecha).replace(/\/(\d{2})(\d{2})$/, "/$2"))
                    .join(" – ")
        }
    ];

    /*
        Antes habia que adivinar que la fila entera era clicable. Ahora cada renglon
        trae su boton y dice lo que va a pasar: pasar lista si a ese horario le toca
        hoy, o ver lo que ya se registro.
    */
    const columnaAccion: Column = {
        overrideHeaderStyle: tituloALaIzquierda,
        overrideCellStyle: tituloALaIzquierda,
        id: "accion",
        header: "",
        accessor: "",
        render: (schedule: ScheduleDTO) => {
            const hoy = leTocaHoy(schedule.sessions) && schedule.status === "Activo";

            /*
                Sin alumnos matriculados no hay nada que abrir: antes el renglon se
                podia clicar igual y no pasaba nada, que es de las cosas que mas
                desconciertan.
            */
            if (!schedule.hasEnrollments) {
                return (
                    <button type="button" className="waAccionFila" disabled={true}>
                        Sin alumnos
                    </button>
                );
            }

            return (
                <span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
                    {hoy && tieneAula(schedule) && (!esMentor || schedule.mentorId === userProfile.id)
                        ? <BotonAbrirAula horario={schedule} className="waAccionFila principal" />
                        : null}
                    <button
                        type="button"
                        className={hoy && !tieneAula(schedule) ? "waAccionFila principal" : "waAccionFila"}
                        disabled={idleLocked}
                        onClick={() => scheduleClick(schedule)}
                    >
                        {hoy ? "Pasar lista" : "Ver asistencias"}
                    </button>
                    {esMentor && schedule.mentorId === userProfile.id
                        ? <BotonPortalClase horario={schedule} className="waAccionFila" />
                        : null}
                </span>
            );
        }
    };

    const columnaEstatus: Column = columnasDeGestion.filter(c => c.id === "status")[0];

    const columns: Column[] = (esMentor
        ? columnasComunes.concat([columnaEstatus])
        : columnasComunes.concat(columnasDeGestion)).concat([columnaAccion]);

    //functions
    const initFn = () => {
        getSchedules();
    };

    const getSchedules = async (url?: string) => {
        try {
            dispatch(enableIdleLockSwitch());
            setSelectedSchedule(null)
            setSchedules([]);

            const perPage: number = userProfile.currentRole === ROLE.MENTOR ? mentorItemsSize : itemsPerPage;
            /*
                Antes al profesor solo se le traian los horarios activos, asi que no
                tenia forma de consultar lo que ya dio. Ahora vienen todos y la vista
                los separa: los vigentes arriba y el resto en "Clases anteriores".
            */
            const criteria: object | string = userProfile.currentRole === ROLE.MENTOR
                ? { "mentorId": userProfile.id, ...criteriosRapidos() }
                : { ...(typeof filters === "object" ? filters : {}), ...criteriosRapidos() };
            const result: any = url
                ? await http.scheduleService.getPaginatedSchedules(url, criteria)
                : await http.scheduleService.getSchedulesByCampusId(campusRef[currentUserCampus].id, perPage, criteria);
            const status = result.status;

            if (status == 200 || status == 204) {
                let unlinkedClassroomsFound: boolean = false;

                let schedulesData = result.data.data.map((schedule: ScheduleDTO) => {
                    if (schedule.building == null || schedule.period == null) {
                        unlinkedClassroomsFound = true
                        return;
                    }
                    return schedule
                });

                setSchedules(schedulesData);
                updatePaginationInfo(result.data)

                if (unlinkedClassroomsFound) {
                    displayError(unlinkedClassroomsFoundMessage);
                } else {
                    setSectionLoaded(true);
                }
            } else {
                displayError(initError)
            }
        }
        catch (error) {
            console.log(error);
            displayError(initError)
        } finally {
            dispatch(disableIdleLockSwitch())
        }
    }

    const displayError = (message: string) => {
        dispatch(
            displayNotice({
                cornerClose: false,
                message: message,
                heading:
                    <h3 style={{ color: "var(--wa-rojo-error)", display: "inline-block" }}>
                        Error
                    </h3>
            })
        );
    };

    const displayNoResults = (message: string) => {
        dispatch(
            displayNotice({
                cornerClose: false,
                message: message,
                heading:
                    <h3 style={{ color: "var(--wa-tinta)", display: "inline-block" }}>
                        No se encontraron resultados
                    </h3>
            })
        );
    };

    const miniMessage = (header: string, message: string) => {
        setDataInfoModal({
            title: header,
            content: (
                <span>
                    {message}
                </span>
            )
        })
        setShowInfoModal(true);
    }

    const closeInfoModal = () => {
        setShowInfoModal(false);
        setDataInfoModal({});
    }

    const handlePageSizeChange = (newPageSize: number) => {
        setItemsPerPage(newPageSize);
    }

    const traerResumenDelDia = async () => {
        if (!campusRef || !campusRef[currentUserCampus]) return;

        try {
            setCargandoDia(true);

            const respuesta = await http.attendanceService.getDaySummary(
                campusRef[currentUserCampus].id,
                diaDelTablero || undefined
            );

            setResumenDelDia(respuesta.data);
        } catch (err: any) {
            console.error(err);
            //El tablero es de apoyo: si falla, la lista de abajo sigue sirviendo.
            setResumenDelDia(null);
        } finally {
            setCargandoDia(false);
        }
    };

    /*
        Abrir el pase de lista desde el tablero. El modal solo necesita estos
        campos del horario (id, nombre, curso, modulo, aula y periodo), asi que se
        arma con lo que trae la clase del dia y no hay que volver a pedir el
        horario entero.
    */
    const pasarListaDelDia = (clase: DayClassDTO) => {
        setSelectedSchedule({
            scheduleId: clase.scheduleId,
            name: clase.schedule,
            course: clase.course,
            level: clase.level,
            building: clase.building,
            classroom: clase.classroom,
            period: clase.period,
            hasEnrollments: true
        } as ScheduleDTO);
    };

    const scheduleClick = (schedule: ScheduleDTO) => {
        if (schedule.hasEnrollments) {
            setSelectedSchedule(schedule);
        } else {
            setNoStudentsEnrolledForSchedule(schedule.scheduleId)
            setTimeout(() => setNoStudentsEnrolledForSchedule(0), 2000)
        }
    }

    const handleDownloadReport = async () => {
        try {
            dispatch(enableIdleLockSwitch());
            await dispatch(handleMentorsSalaryXlsx({
                campusId: campusRef[currentUserCampus].id,
                criteria: {
                    status: 1,
                    startDate: startDate,
                    endDate: endDate
                }
            })).unwrap();
        } catch (error: any) {
            if (error.status == 400) {
                displayNoResults("No se encontró información para las fechas seleccionadas");
            } else {
                displayError("Hubo un error al tratar de obtener el documento!");
            }
        }
        finally {
            dispatch(disableIdleLockSwitch());
        }
    }

    const confirmDownloadReport = (callback: () => void) => {
        dispatch(displayNotice({
            procedure: async () => { callback() },
            message: `¿Desea descargar la planilla de profesores del día ${startDate ? new Date(startDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }) : ''} al día ${endDate ? new Date(endDate).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}?`,
            heading: <h3 style={{ color: "var(--wa-tinta)", display: "inline-block" }}>
                Confirme
            </h3>
        }))
    }
    //useEffect
    useEffect(() => {
        if (sessionToken) {
            currentUserCampus == null ? displayError(noCampuses) : initFn();
        }
    }, []);

    useEffect(() => {
        if ((sectionLoaded && currentUserCampus) && (currentUserCampus != userCampus)) {
            dispatch(enableIdleLockSwitch());
            setSectionLoaded(false);
            localStorage.setItem("currentCampus", currentUserCampus)
            setUserCampus(currentUserCampus)
            setSelectedSchedule(null)
            setSchedules([]);
            initFn();
        }
    }, [currentUserCampus, sectionLoaded]);

    useEffect(() => {
        if (sessionToken) {
            getSchedules();
        }
    }, [itemsPerPage]);

    useEffect(() => {
        initFn();
    }, [filters]);

    //Al soltar o volver a poner una ficha se vuelve a pedir la lista.
    useEffect(() => {
        if (sessionToken && sectionLoaded) {
            initFn();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rapidos]);

    useEffect(() => {
        fetchRoleData();
    }, [currentRole]);

    useEffect(() => {
        const horarioEnlazado: string | null = parametrosUrl.get("horario");

        if (horarioEnlazado == null || enlaceAtendido.current === horarioEnlazado) return;
        if (!campusRef || !campusRef[currentUserCampus]) return;

        enlaceAtendido.current = horarioEnlazado;

        const abrir = async () => {
            try {
                dispatch(enableIdleLockSwitch());

                const resultado: any = await http.scheduleService.getSchedulesByCampusId(
                    campusRef[currentUserCampus].id, 1, { id: +horarioEnlazado }
                );
                const encontrado = resultado?.data?.data?.[0];

                if (encontrado) {
                    setSelectedSchedule(encontrado);
                } else {
                    displayError("Ese horario no está en este plantel.");
                }
            } catch (err: any) {
                console.error(err);
                displayError("El horario del enlace no pudo abrirse.");
            } finally {
                dispatch(disableIdleLockSwitch());

                //Se limpia la direccion para que al cerrar no se vuelva a abrir solo.
                const limpios = new URLSearchParams(parametrosUrl);
                limpios.delete("horario");
                setParametrosUrl(limpios, { replace: true });
            }
        };

        abrir();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parametrosUrl, currentUserCampus, campusRef]);

    /*
        El tablero solo tiene sentido para quien coordina: al profesor no hay que
        decirle si el mismo paso lista. Se refresca al cambiar de plantel y cada
        vez que se cierra una ficha de asistencias, que es justo cuando el numero
        acaba de cambiar.
    */
    useEffect(() => {
        if (sessionToken && userProfile.currentRole !== ROLE.MENTOR && selectedSchedule == null) {
            traerResumenDelDia();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUserCampus, selectedSchedule, sessionToken, diaDelTablero]);

    return (
        <AttendancesView
            rapidos={rapidos}
            onRapidos={setRapidos}
            panelFiltros={panelFiltros}
            onPanelFiltros={() => setPanelFiltros(!panelFiltros)}
            sectionLoaded={sectionLoaded}
            schedules={schedules}
            setSchedules={setSchedules}
            selectedSchedule={selectedSchedule}
            setSelectedSchedule={setSelectedSchedule}
            displayError={displayError}
            columns={columns}
            filters={filters}
            onFilterChange={onFilterChange}
            onClearFilters={onClearFilters}
            itemsPerPage={itemsPerPage}
            handlePageSizeChange={handlePageSizeChange}
            pages={pages}
            pagesSize={(userProfile.currentRole === ROLE.MENTOR) ? [mentorItemsSize] : pagesSize}
            itemCount={itemCount}
            showInfoModal={showInfoModal}
            dataInfoModal={dataInfoModal}
            closeInfoModal={closeInfoModal}
            miniMessage={miniMessage}
            scheduleClick={scheduleClick}
            idleLocked={idleLocked}
            userProfile={userProfile}
            reportProps={{
                handleDownloadReport: handleDownloadReport,
                title: Constants.Common.payroll,
                showModal: showReportModal,
                closeModal: () => { setShowReportModal(false) },
                openModal: () => { setShowReportModal(true) },
                startDate: startDate,
                endDate: endDate,
                onChange: (dates) => {
                    const [start, end] = dates;
                    setStartDate(start);
                    setEndDate(end);
                },
                confirmDownloadReport: confirmDownloadReport
            }}
            tableroProps={{
                dia: resumenDelDia,
                plantel: campusRef ? campusRef[currentUserCampus] : null,
                cargando: cargandoDia,
                onPasarLista: pasarListaDelDia,
                onRecargar: traerResumenDelDia,
                onCambiarDia: setDiaDelTablero,
                enHoy: diaDelTablero == null
            }}
            searchProps={{
                search: searchValue,
                onChangeSearch,
                onClickSearch,
                onClearSearch
            }}
        />
    );
}

export default AttendancesController